import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useProjects } from "@/hooks/useProjects";
import { useTaskMutations } from "@/hooks/useTasks";
import { useTaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import { useTaskSubscription } from "@/hooks/useTaskSubscription";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useUsers } from "@/hooks/useUsers";
import { usePlots } from "@/hooks/usePlots";
import { useProcurementPackages } from "@/hooks/useProcurementPackages";
import { useSections } from "@/hooks/useSections";
import { useAllLocations } from "@/hooks/useAllLocations";
import { useMyProjectIds } from "@/hooks/useMyProjectIds";
import { useTeamsAuth } from "@/components/providers/TeamsAuthProvider";
import { useAuth } from "@/contexts/AuthContext";
import { TaskStatus, type TaskExtended } from "@/types";
import { useCascadingFilterScopes } from "@/components/tasks/hooks/useCascadingFilterScopes";
import { useServerFilterParams, buildBucketFilterParams } from "@/components/tasks/hooks/useServerFilterParams";
import { useTaskWeekBuckets } from "@/hooks/useTaskWeekBuckets";
import { WeekSelector } from "@/components/tasks/WeekSelector";
import { useColumnRefetch } from "@/components/tasks/hooks/useColumnRefetch";
import { CreateTaskDialog } from "@/components/tasks/CreateTaskDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MultiSelectDropdown } from "@/components/ui/multi-select-dropdown";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { KanbanColumn } from "@/components/tasks/KanbanColumn";
import { MemoListStack } from "@/components/memo/MemoListStack";
import { SearchIcon, CloseIcon, SortIcon, SortAscIcon, SortDescIcon, AddIcon, FiltersIcon } from "@/lib/icons";
import { MemoListToggle } from "@/components/memo/MemoListToggle";
import { Badge } from "@/components/ui/badge";
import { useTaskFilterStore, type SortBy } from "@/stores/useTaskFilterStore";
import { TASK_STATUS_LABELS_SINGULAR } from "@/lib/taskStatusMeta";
import { KANBAN_COLUMNS, SORT_OPTIONS, TASK_TYPE_OPTIONS } from "@/lib/taskBoardMeta";

export function Tehtavahallinta() {
  const { projects, loading: projectsLoading } = useProjects();
  const { users } = useUsers();
  const { plots } = usePlots();
  const { procurementPackages } = useProcurementPackages();
  const { sections } = useSections();
  const { locations } = useAllLocations();
  const { user } = useTeamsAuth();
  const { dbUser } = useAuth();

  // ADMIN / INTERNAL+ see every hanke server-side without holding a project
  // role, so the "omat hankkeet" narrowing below must not apply to them.
  const seesAllProjects =
    dbUser?.accessLevel === "ADMIN" || dbUser?.accessLevel === "INTERNAL_PLUS";

  // Resolve the current user's DB id once (mapping from Azure object id).
  // Needed by the "only my projects" filter and consumed further below.
  const currentUserDbId = useMemo(() => {
    if (!user?.id) return undefined;
    return users.find((u) => u.azureObjectId === user.id)?.id;
  }, [users, user?.id]);

  // Fetch the set of project IDs where the current user has a role. Used to
  // dim / auto-prune projects when the "Vain omat hankkeet" toggle is on.
  const { myProjectIdSet, loading: myProjectRolesLoading } =
    useMyProjectIds(currentUserDbId);

  // Zustand store for persisted filter/sort state
  const {
    search,
    projectFilter,
    viewFilter,
    onlyMyProjects,
    plotFilter,
    assignedToFilter,
    deadlineWeek,
    procurementPackageFilter,
    workSectionFilter,
    locationFilter,
    taskTypeFilter,
    statusFilter,
    sortBy,
    sortDirection,
    setSearch,
    setProjectFilter,
    setViewFilter,
    setPlotFilter,
    setAssignedToFilter,
    setDeadlineWeek,
    setProcurementPackageFilter,
    setWorkSectionFilter,
    setLocationFilter,
    setTaskTypeFilter,
    setStatusFilter,
    setSortBy,
    toggleSortDirection,
    resetFilters,
  } = useTaskFilterStore();

  // Backend search: debounced — committed 300 ms after the user stops typing.
  const committedSearch = useDebouncedValue(search, 300);

  // serverFilterParams and useTasks are defined below ppNameMap/sectionNameMap (which they depend on)

  // Track whether we have ever loaded data — once true, we never show the full-page skeleton again
  const hasLoadedOnce = useRef(false);

  // Dialog state (not persisted — local only)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskExtended | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Column refetch refs and helpers
  const { refs: refetchRefs, refetchAll: refetchAllColumns, refetchColumn } = useColumnRefetch();

  // Id→name maps for project and plot (for display on cards)
  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [projects]);
  const plotNameById = useMemo(() => {
    const m = new Map<string, string>();
    plots.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [plots]);
  const locationNameById = useMemo(
    () =>
      new Map(
        locations.map((l) => [
          l.id,
          l.hierarchyPath.length > 0 ? l.hierarchyPath.join(" › ") : l.name,
        ]),
      ),
    [locations],
  );

  // Cascading filter scopes and option builders
  const {
    validProjectIdSet,
    validPlotIdSet,
    validPpKeySet,
    validSectionKeySet,
    ppOptions,
    ppNameMap,
    sectionOptions,
    sectionNameMap,
    validLocationIdSet,
    locationOptions,
  } = useCascadingFilterScopes({
    projects,
    plots,
    procurementPackages,
    sections,
    locations,
    myProjectIdSet,
    myProjectRolesLoading,
    seesAllProjects,
  });

  // Server filter params
  const serverFilterParams = useServerFilterParams({
    committedSearch,
    validProjectIdSet,
    validPlotIdSet,
    validPpKeySet,
    validSectionKeySet,
    validLocationIdSet,
    ppNameMap,
    sectionNameMap,
    myProjectRolesLoading,
    seesAllProjects,
    currentAzureUserId: user?.id,
    users,
  });

  // Week-bucket aggregation for the WeekSelector. Strips the deadline-week
  // selection so the chips reflect what's present absent that filter.
  const bucketFilterParams = useMemo(
    () => buildBucketFilterParams(serverFilterParams),
    [serverFilterParams],
  );
  const {
    buckets,
    totalCount: weekTotalCount,
    noDeadlineCount,
    loading: bucketsLoading,
    refetch: refetchBuckets,
  } = useTaskWeekBuckets(bucketFilterParams);

  const { updateTaskStatus, createTask } = useTaskMutations();

  // Refetch the week buckets alongside the kanban so chip counts and overdue
  // dots stay in sync with task create/update/delete and real-time events.
  const refetchAllColumnsAndBuckets = useCallback(() => {
    refetchAllColumns();
    refetchBuckets();
  }, [refetchAllColumns, refetchBuckets]);
  const toggleTaskCompleted = useTaskCompletionToggle(
    refetchAllColumnsAndBuckets,
  );
  const refetchColumnAndBuckets = useCallback(
    (status: TaskStatus) => {
      refetchColumn(status);
      refetchBuckets();
    },
    [refetchColumn, refetchBuckets],
  );

  // Subscribe to real-time task change events via WebSocket.
  useTaskSubscription(
    serverFilterParams.projectIds,
    refetchAllColumnsAndBuckets,
    refetchColumnAndBuckets,
  );

  // Stable identity for the full filter+sort state. Included in each
  // KanbanColumn's key so the virtualizer is rebuilt from scratch when the
  // task set can reorder substantially — prevents stale size-cache entries
  // from causing overlapping cards.
  const columnResetKey = useMemo(
    () => JSON.stringify(serverFilterParams),
    [serverFilterParams],
  );

  // Per-column effective status set: intersect each column's native status
  // set with `statusFilter`. When the intersection is empty, KanbanColumn
  // skips its query and renders an empty column — so preset chips like
  // "Odottavat aliurakoitsijaa" visually constrain the board to the one column that can
  // contain the selected status.
  const effectiveColumns = useMemo(() => {
    if (statusFilter.length === 0) return KANBAN_COLUMNS;
    return KANBAN_COLUMNS.map((col) => {
      const native = col.statuses ?? [col.status];
      const intersected = native.filter((s) => statusFilter.includes(s));
      return { ...col, statuses: intersected };
    });
  }, [statusFilter]);

  // Once projects have loaded, never show the full-page skeleton again.
  useEffect(() => {
    if (!projectsLoading) {
      hasLoadedOnce.current = true;
    }
  }, [projectsLoading]);

  const initialLoading = !hasLoadedOnce.current && projectsLoading;

  const hasFilters =
    committedSearch ||
    search ||
    projectFilter.length > 0 ||
    viewFilter !== "Kaikkien" ||
    !onlyMyProjects ||
    plotFilter.length > 0 ||
    assignedToFilter.length > 0 ||
    procurementPackageFilter.length > 0 ||
    workSectionFilter.length > 0 ||
    locationFilter.length > 0 ||
    taskTypeFilter.length > 0 ||
    statusFilter.length > 0 ||
    sortBy !== "deadline" ||
    sortDirection !== "asc";

  // Count only “facet” filters (entity + deadline + taskType) for the button badge
  const facetFilterCount =
    projectFilter.length +
    plotFilter.length +
    procurementPackageFilter.length +
    workSectionFilter.length +
    locationFilter.length +
    assignedToFilter.length +
    taskTypeFilter.length +
    (!onlyMyProjects ? 1 : 0);

  // Filter sheet dialog open state
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Build pill chips for currently active facet filters
  type Chip = { key: string; label: string; onRemove: () => void };
  const activeChips = useMemo<Chip[]>(() => {
    const chips: Chip[] = [];
    projectFilter.forEach((id) => {
      const name = projects.find((p) => p.id === id)?.name || id;
      chips.push({
        key: `project-${id}`,
        label: `Hanke: ${name}`,
        onRemove: () => setProjectFilter(projectFilter.filter((x) => x !== id)),
      });
    });
    plotFilter.forEach((id) => {
      const name = plots.find((p) => p.id === id)?.name || id;
      chips.push({
        key: `plot-${id}`,
        label: `Tontti: ${name}`,
        onRemove: () => setPlotFilter(plotFilter.filter((x) => x !== id)),
      });
    });
    procurementPackageFilter.forEach((key) => {
      const opt = ppOptions.find((o) => o.value === key);
      chips.push({
        key: `pp-${key}`,
        label: `Hankintapaketti: ${opt?.label || key}`,
        onRemove: () =>
          setProcurementPackageFilter(procurementPackageFilter.filter((x) => x !== key)),
      });
    });
    workSectionFilter.forEach((key) => {
      const opt = sectionOptions.find((o) => o.value === key);
      chips.push({
        key: `ws-${key}`,
        label: `Työvaihe: ${opt?.label || key}`,
        onRemove: () =>
          setWorkSectionFilter(workSectionFilter.filter((x) => x !== key)),
      });
    });
    locationFilter.forEach((id) => {
      chips.push({
        key: `location-${id}`,
        label: `Sijainti: ${locationNameById.get(id) ?? id}`,
        onRemove: () =>
          setLocationFilter(locationFilter.filter((x) => x !== id)),
      });
    });
    assignedToFilter.forEach((id) => {
      const u = users.find((usr) => usr.id === id);
      const name = u ? (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email) : id;
      chips.push({
        key: `user-${id}`,
        label: `Vastuu: ${name}`,
        onRemove: () => setAssignedToFilter(assignedToFilter.filter((x) => x !== id)),
      });
    });
    taskTypeFilter.forEach((tt) => {
      const label = TASK_TYPE_OPTIONS.find((o) => o.value === tt)?.label || tt;
      chips.push({
        key: `tt-${tt}`,
        label: `Tyyppi: ${label}`,
        onRemove: () => setTaskTypeFilter(taskTypeFilter.filter((x) => x !== tt)),
      });
    });
    statusFilter.forEach((st) => {
      const label = TASK_STATUS_LABELS_SINGULAR[st] || st;
      chips.push({
        key: `st-${st}`,
        label: `Tila: ${label}`,
        onRemove: () => setStatusFilter(statusFilter.filter((x) => x !== st)),
      });
    });
    return chips;
  }, [
    projectFilter,
    plotFilter,
    procurementPackageFilter,
    workSectionFilter,
    locationFilter,
    assignedToFilter,
    taskTypeFilter,
    statusFilter,
    projects,
    plots,
    ppOptions,
    sectionOptions,
    locationNameById,
    users,
    setProjectFilter,
    setPlotFilter,
    setProcurementPackageFilter,
    setWorkSectionFilter,
    setLocationFilter,
    setAssignedToFilter,
    setTaskTypeFilter,
    setStatusFilter,
  ]);

  const openTaskDialog = (task: TaskExtended) => {
    setSelectedTask(task);
    setDialogOpen(true);
  };

  // When closing the dialog, refetch the column containing the task so the
  // card's subtaskProgress reflects any subtask edits made inside the dialog.
  // useTaskSubscription only refetches on parent-status transitions, so
  // intermediate subtask progress changes (e.g. 1/3 → 2/3, still IN_PROGRESS)
  // wouldn't otherwise propagate to the card until a page reload.
  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      if (!open && selectedTask) {
        refetchColumn(selectedTask.status);
        refetchBuckets();
      }
    },
    [selectedTask, refetchColumn, refetchBuckets],
  );

  const handleUpdateStatus = async (taskId: string, status: TaskStatus) => {
    try {
      if (selectedTask?.id === taskId && selectedTask.version != null) {
        await updateTaskStatus(taskId, status, selectedTask.version);
        // Column refetch is handled by the subscription (useTaskSubscription).
      }
      setDialogOpen(false);
    } catch (error) {
      console.error("Failed to update task status:", error);
    }
  };

// Active users for the assigned-to dropdown
  const activeUsers = useMemo(
    () => users.filter((u) => u.isActive),
    [users],
  );

  // Find current user's internal ID for the create dialog
  const currentUserId = useMemo(() => {
    if (!user?.id) return "";
    const currentUser = users.find((u) => u.azureObjectId === user.id);
    return currentUser?.id || "";
  }, [user?.id, users]);

  if (initialLoading) {
    return (
      <div className="flex flex-col h-[calc(100dvh-48px)]">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-background shrink-0 -mx-6 -mt-6">
          <Skeleton className="h-7 w-40 mr-2" />
          <Skeleton className="h-9 w-[156px]" />
          <Skeleton className="h-10 w-10 rounded-md" />
          <Skeleton className="h-10 w-48 rounded-md" />
          <div className="flex-1 min-w-[200px] max-w-sm">
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
          <Skeleton className="h-9 w-36 ml-auto rounded-md" />
        </div>
        {/* Preset chips */}
        <div className="shrink-0 flex flex-wrap items-center gap-2 pt-3 pb-3">
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-16 rounded-full" />
          <Skeleton className="h-7 w-28 rounded-full" />
          <Skeleton className="h-7 w-28 rounded-full" />
          <Skeleton className="h-7 w-44 rounded-full" />
          <Skeleton className="h-7 w-24 rounded-full" />
        </div>
        {/* Kanban board */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-0 flex-1">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-6 w-24" />
              {[1, 2, 3].map((j) => (
                <Skeleton key={j} className="h-32 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-48px)]">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-background shrink-0 -mx-6 -mt-6">
        <h1 className="text-lg font-semibold mr-2">
          Tehtävähallinta
        </h1>
        <Tabs value={viewFilter} onValueChange={(v) => setViewFilter(v as "Kaikkien" | "Minun")} className="w-auto">
          <TabsList className="grid w-[168px] grid-cols-2 border border-input bg-muted/60 shadow-sm">
            <TabsTrigger
              value="Minun"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:hover:text-foreground"
            >
              Minun
            </TabsTrigger>
            <TabsTrigger
              value="Kaikkien"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:hover:text-foreground"
            >
              Kaikkien
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="h-6 w-px bg-border" aria-hidden="true" />
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={'Hae nimellä, käyttäjällä, sijainnilla...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="relative">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setFilterSheetOpen(true)}
            title="Suodata"
          >
            <FiltersIcon className="h-4 w-4" />
          </Button>
          {facetFilterCount > 0 && (
            <Badge
              variant="secondary"
              className="pointer-events-none absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 text-[9px] tabular-nums"
            >
              {facetFilterCount}
            </Badge>
          )}
        </div>
        <div className="flex h-10 rounded-md border border-input">
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
            <SelectTrigger className="w-[160px] h-full border-0 rounded-none rounded-l-md border-r border-input focus:ring-0 focus:ring-offset-0 [&>span]:flex-1 [&>span]:text-left">
              <SortIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Järjestä" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSortDirection}
            className="h-full px-3 rounded-none rounded-r-md"
            title={sortDirection === "asc" ? "Nouseva järjestys" : "Laskeva järjestys"}
          >
            {sortDirection === "asc" ? (
              <SortDescIcon className="h-4 w-4" />
            ) : (
              <SortAscIcon className="h-4 w-4" />
            )}
          </Button>
        </div>
        <MemoListToggle />
        <Button onClick={() => setCreateDialogOpen(true)} size="sm" className="ml-auto">
          <AddIcon className="mr-2 h-4 w-4" />
          Luo uusi tehtävä
        </Button>
      </div>
      <div className="shrink-0 space-y-3 pt-3 pb-3">
        {/* Row 2a: Week selector (replaces the deadline preset chips) */}
        <WeekSelector
          value={deadlineWeek}
          onChange={setDeadlineWeek}
          buckets={buckets}
          totalCount={weekTotalCount}
          noDeadlineCount={noDeadlineCount}
          loading={bucketsLoading}
          showScrollButtons
        />

        {/* Row 3: Active filter chips */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {activeChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1.5 rounded-full bg-accent/60 px-2.5 py-1 text-xs text-accent-foreground border border-border/40 animate-in fade-in slide-in-from-top-1 duration-200"
              >
                <span className="truncate max-w-[220px]">{chip.label}</span>
                <button
                  type="button"
                  onClick={chip.onRemove}
                  className="rounded-full p-0.5 hover:bg-background/80 transition-colors"
                  aria-label={`Poista suodatin: ${chip.label}`}
                >
                  <CloseIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => resetFilters()}
            >
              Tyhjennä kaikki
            </Button>
          </div>
        )}
      </div>

      {/* Filter sheet dialog */}
      <Dialog open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Suodata tehtäviä</DialogTitle>
            <DialogDescription>
              Rajaa näkymää hankkeen, tontin, hankintapaketin tai muiden kenttien mukaan.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            {/* Cascading filter group: project → plot → procurement package →
                work section. Grouped visually so the 4 hierarchical filters
                read as one unit, distinct from the task-attribute filters
                below. */}
            <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-x-5 gap-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Hanke</Label>
                <MultiSelectDropdown
                  options={projects.map((p) => ({
                    value: p.id,
                    label: p.name,
                    disabled: !validProjectIdSet.has(p.id),
                  }))}
                  value={projectFilter}
                  onChange={setProjectFilter}
                  placeholder="Kaikki hankkeet"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Tontti</Label>
                <MultiSelectDropdown
                  options={plots.map((plot) => ({
                    value: plot.id,
                    label: plot.name,
                    disabled: !validPlotIdSet.has(plot.id),
                  }))}
                  value={plotFilter}
                  onChange={setPlotFilter}
                  placeholder="Kaikki tontit"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Hankintapaketti</Label>
                <MultiSelectDropdown
                  options={ppOptions.map((opt) => ({
                    ...opt,
                    disabled: !validPpKeySet.has(opt.value),
                  }))}
                  value={procurementPackageFilter}
                  onChange={setProcurementPackageFilter}
                  placeholder="Kaikki hankintapaketit"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Työvaihe</Label>
                <MultiSelectDropdown
                  options={sectionOptions.map((opt) => ({
                    ...opt,
                    disabled: !validSectionKeySet.has(opt.value),
                  }))}
                  value={workSectionFilter}
                  onChange={setWorkSectionFilter}
                  placeholder="Kaikki työvaiheet"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Sijainti</Label>
                <MultiSelectDropdown
                  options={locationOptions.map((opt) => ({
                    ...opt,
                    disabled: !validLocationIdSet.has(opt.value),
                  }))}
                  value={locationFilter}
                  onChange={setLocationFilter}
                  placeholder="Kaikki sijainnit"
                  className="w-full"
                />
              </div>
            </div>

            {/* Task attribute group: independent facets, 3 equal columns. */}
            <div className="mt-6 grid grid-cols-1 items-start gap-x-5 gap-y-4 border-t pt-6 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Vastuuhenkilö</Label>
                <MultiSelectDropdown
                  options={activeUsers.map((u) => ({
                    value: u.id,
                    label:
                      u.firstName && u.lastName
                        ? `${u.firstName} ${u.lastName}`
                        : u.email,
                  }))}
                  value={assignedToFilter}
                  onChange={setAssignedToFilter}
                  placeholder="Kaikki henkilöt"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Tehtävätyyppi</Label>
                <MultiSelectDropdown
                  options={TASK_TYPE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  value={taskTypeFilter}
                  onChange={setTaskTypeFilter}
                  placeholder="Kaikki tehtävätyypit"
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button
              variant="ghost"
              onClick={() => resetFilters()}
              disabled={!hasFilters}
            >
              Tyhjennä kaikki
            </Button>
            <Button onClick={() => setFilterSheetOpen(false)}>Valmis</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kanban Board — grows to fill remaining space.
          The key includes sort params so changing sort remounts each column:
          this gives the virtualizer a fresh size cache, preventing stale
          measurements from causing cards to render on top of each other. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-0 flex-1">
        {effectiveColumns.map((column) => (
          <KanbanColumn
            key={`${column.status}-${columnResetKey}`}
            column={column}
            filterParams={serverFilterParams}
            projectNameById={projectNameById}
            plotNameById={plotNameById}
            onTaskClick={openTaskDialog}
            onToggleCompleted={toggleTaskCompleted}
            topSlot={column.status === TaskStatus.PENDING ? <MemoListStack /> : undefined}
            refetchRef={
              column.status === TaskStatus.PENDING
                ? refetchRefs.pending
                : column.status === TaskStatus.IN_PROGRESS
                  ? refetchRefs.inProgress
                  : refetchRefs.completed
            }
          />
        ))}
      </div>

      {/* Task Dialog */}
      <TaskDialog
        task={selectedTask}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        onUpdateStatus={handleUpdateStatus}
        currentUserId={currentUserId}
        onTaskUpdated={setSelectedTask}
        onSubtasksChanged={refetchAllColumns}
      />

      {/* Create Task Dialog */}
      <CreateTaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        projects={projects}
        plots={plots}
        procurementPackages={procurementPackages}
        users={users}
        currentUserId={currentUserId}
        createTask={createTask}
        onTaskCreated={(task) => {
          setSelectedTask(task);
          setDialogOpen(true);
        }}
        onTasksRefresh={refetchAllColumns}
      />
    </div>
  );
}
